import * as React from 'react';

import {
  enumUnit,
  MoneyInput,
  Weight,
} from '@rematter/frontend-lib-api-client';
import {
  AsyncBoundary,
  Button,
  Col,
  ConsoleDrawer,
  formatMoney,
  MeasuredValue,
  Row,
  TextArea,
  TextField,
  TinyTable,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { loadUpdateAtom } from '../../_graphql/mutationLoadUpdate';
import { purchaseLoadUpdateAtom } from '../../_graphql/mutationPurchaseLoadUpdate';
import * as state from '../../_state/transactions.state';
import { useTranslations } from '../../i18n/useTranslations';
import { enumLoadWeightType, LoadLineItemRecord } from '../../types/load';
import { PurchaseLoadRecord } from '../../types/purchaseLoad';
import {
  calculateNormalizedLineTotals,
  calculateSingleLineTotal,
  NormalizedLineItem,
  normalizeInboundLineItem,
  normalizeOutboundLineItem,
} from '../../utils/weight';

import { FileItem } from './FileItem';

import { useFileDownload } from '~/shared/hooks/useFileDownload';

type MaterialRow = NormalizedLineItem & {
  gross: Weight | null;
  tare: Weight | null;
  net: Weight | null;
  lineTotal: MoneyInput | null;
};

type TransactionSidePanelProps = {
  direction: 'inbound' | 'outbound';
  documentLabel: string;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
};

//
// ⚛️ ⚛️ ⚛️ TRANSACTION SIDE PANEL ⚛️ ⚛️ ⚛️
//
export const TransactionSidePanel = ({
  direction,
  documentLabel,
  isOpen,
  onOpenChange,
}: TransactionSidePanelProps) => {
  // Translations
  const t = useTranslations();

  // States
  const [externalNotes, setExternalNotes] = React.useState('');
  const [internalNotes, setInternalNotes] = React.useState('');
  const [truckNumber, setTruckNumber] = React.useState('');
  const [trailerNumber, setTrailerNumber] = React.useState('');
  const [isSaving, setIsSaving] = React.useState(false);

  // Atoms
  const outboundDetail = useAtomValue(state.$selectedOutboundTransactionDetail);
  const inboundDetail = useAtomValue(state.$selectedTransactionDetail);
  const { data: outboundPhotos } = useAtomValue(
    state.$selectedOutboundTransactionPhotos,
  );
  const { data: inboundPhotos } = useAtomValue(
    state.$selectedTransactionPhotos,
  );
  const outboundUpdateMutation = useSetAtom(loadUpdateAtom({}));
  const inboundUpdateMutation = useSetAtom(purchaseLoadUpdateAtom({}));

  // Data hooks
  const { downloadFile } = useFileDownload();
  const toast = useToast();

  // Memoized values
  const isOutbound = direction === 'outbound';

  const detail = isOutbound ? outboundDetail : inboundDetail;
  const isFetching = detail.isFetching;
  const error = detail.error;

  const outboundLoad = React.useMemo(
    () => (isOutbound ? (outboundDetail.data?.load ?? null) : null),
    [isOutbound, outboundDetail.data],
  );

  const inboundLoad = React.useMemo(
    () =>
      !isOutbound ? (inboundDetail.data as PurchaseLoadRecord | null) : null,
    [isOutbound, inboundDetail.data],
  );

  const files = React.useMemo(
    () => (isOutbound ? (outboundPhotos ?? []) : (inboundPhotos ?? [])),
    [isOutbound, outboundPhotos, inboundPhotos],
  );

  const formSource = React.useMemo(() => {
    if (isOutbound && outboundLoad) {
      return {
        externalNotes: outboundLoad.recyclerExternalNotes ?? '',
        internalNotes: outboundLoad.recyclerInternalNotes ?? '',
        truckNumber: outboundLoad.truckNumber ?? '',
        trailerNumber: outboundLoad.trailerNumber ?? '',
      };
    }
    if (!isOutbound && inboundLoad) {
      return {
        externalNotes: inboundLoad.externalNotes ?? '',
        internalNotes: inboundLoad.internalNotes ?? '',
        truckNumber: inboundLoad.truckNumber ?? '',
        trailerNumber: inboundLoad.trailerNumber ?? '',
      };
    }
    return null;
  }, [isOutbound, outboundLoad, inboundLoad]);

  const { rows: materialRows, totals } = React.useMemo(() => {
    const normalized: NormalizedLineItem[] = isOutbound
      ? (outboundLoad?.lineItems ?? []).map((li) =>
          normalizeOutboundLineItem(li as LoadLineItemRecord),
        )
      : (inboundLoad?.lineItems ?? []).map(normalizeInboundLineItem);

    const rows: MaterialRow[] = normalized.map((item) => ({
      ...item,
      gross: item.weights?.[enumLoadWeightType.Gross] ?? null,
      tare: item.weights?.[enumLoadWeightType.Tare] ?? null,
      net: item.weights?.[enumLoadWeightType.Net] ?? null,
      lineTotal: calculateSingleLineTotal(item),
    }));

    return { rows, totals: calculateNormalizedLineTotals(normalized) };
  }, [isOutbound, outboundLoad, inboundLoad]);

  // Callbacks
  const populateForm = React.useCallback(() => {
    if (formSource) {
      setExternalNotes(formSource.externalNotes);
      setInternalNotes(formSource.internalNotes);
      setTruckNumber(formSource.truckNumber);
      setTrailerNumber(formSource.trailerNumber);
    }
  }, [formSource]);

  const handleSave = React.useCallback(async () => {
    try {
      setIsSaving(true);
      if (isOutbound && outboundLoad) {
        await outboundUpdateMutation({
          id: outboundLoad.id,
          recyclerExternalNotes: externalNotes || null,
          recyclerInternalNotes: internalNotes || null,
          truckNumber: truckNumber || null,
          trailerNumber: trailerNumber || null,
        });
      } else if (!isOutbound && inboundLoad) {
        await inboundUpdateMutation({
          id: inboundLoad.id,
          input: {
            externalNotes: externalNotes || null,
            internalNotes: internalNotes || null,
            truckNumber: truckNumber || null,
            trailerNumber: trailerNumber || null,
          },
        });
      }
      toast.success(t.transactionsView.actions.saveSuccess);
    } catch {
      toast.error(t.transactionsView.actions.saveError);
    } finally {
      setIsSaving(false);
    }
  }, [
    isOutbound,
    outboundLoad,
    inboundLoad,
    externalNotes,
    internalNotes,
    truckNumber,
    trailerNumber,
    outboundUpdateMutation,
    inboundUpdateMutation,
    toast,
    t,
  ]);

  // Effects
  /** Populate form fields from ticket data when the panel opens */
  React.useEffect(() => {
    if (isOpen) {
      populateForm();
    }
  }, [isOpen, populateForm]);

  return (
    <ConsoleDrawer
      title={t.transactionsView.actions.loadTitle(
        documentLabel,
        t.transactionsView.actions.viewAdditionalDetails,
      )}
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      isLoading={isFetching}
      footer={
        <Button variant="solid" onPress={handleSave} isLoading={isSaving}>
          {t.transactionsView.actions.save}
        </Button>
      }
      content={
        <AsyncBoundary loading={isFetching} error={error}>
          <Col gap4 className="@TransactionSidePanel">
            <Col gap3>
              <TextArea
                label={t.transactionsView.actions.externalNotes}
                value={externalNotes}
                onChange={setExternalNotes}
              />
              <TextArea
                label={t.transactionsView.actions.internalNotes}
                value={internalNotes}
                onChange={setInternalNotes}
              />
              <Row gap3>
                <TextField
                  label={t.transactionsView.actions.truckNumber}
                  value={truckNumber}
                  onChange={setTruckNumber}
                  className="flex-1"
                />
                <TextField
                  label={t.transactionsView.actions.trailerNumber}
                  value={trailerNumber}
                  onChange={setTrailerNumber}
                  className="flex-1"
                />
              </Row>
            </Col>

            <Col gap2>
              <h3 className="text-sm font-semibold text-neutral/70">
                {t.transactionsView.actions.files}
              </h3>
              {files.length > 0 ? (
                <Col gap2>
                  {files.map((file) => (
                    <FileItem
                      key={file.id}
                      fileName={file.fileName}
                      mimeType={file.mimeType}
                      onDownload={() =>
                        downloadFile({
                          id: file.id,
                          fileName: file.fileName,
                        })
                      }
                    />
                  ))}
                </Col>
              ) : (
                <p className="text-sm text-neutral/50">
                  {t.transactionsView.actions.noFiles}
                </p>
              )}
            </Col>

            <Col gap2>
              <h3 className="text-sm font-semibold text-neutral/70">
                {t.transactionsView.actions.materials}
              </h3>
              {materialRows.length > 0 ? (
                <MaterialsTable rows={materialRows} totals={totals} t={t} />
              ) : (
                <p className="text-sm text-neutral/50">-</p>
              )}
            </Col>
          </Col>
        </AsyncBoundary>
      }
      className="@TransactionSidePanel"
    />
  );
};

type MaterialsTotals = {
  gross: Weight | null;
  tare: Weight | null;
  net: Weight | null;
  eachCount: number | null;
  lineTotal: MoneyInput | null;
};

type MaterialsTableProps = {
  rows: MaterialRow[];
  totals: MaterialsTotals;
  t: ReturnType<typeof useTranslations>;
};

//
// ⚛️ ⚛️ ⚛️ MATERIALS TABLE ⚛️ ⚛️ ⚛️
//
const MaterialsTable = ({ rows, totals, t }: MaterialsTableProps) => (
  <TinyTable<MaterialRow>
    className="@MaterialsTable"
    headerClassName="py-2 bg-neutral/5"
    rowClassName="py-2 "
    footerClassName="py-2 bg-neutral/5 items-start"
    data={rows}
    isLoading={false}
    rowKey={(row) => row.id}
    columns={[
      {
        key: 'itemName',
        label: t.transactionsView.actions.itemName,
        cell: (row) => (
          <span className="min-w-0 text-neutral">{row.itemName}</span>
        ),
        footer: () => (
          <span className="font-medium text-neutral">
            {t.transactionsView.actions.total}
          </span>
        ),
      },
      {
        key: 'type',
        label: t.transactionsView.actions.type,
        width: 'max-content',
        cell: (row) => <span className="text-neutral/70">{row.type}</span>,
      },
      {
        key: 'gross',
        label: t.transactionsView.actions.grossWeight,
        align: 'right',
        width: 'max-content',
        cell: (row) => (
          <div className="text-right">
            {row.gross ? (
              <MeasuredValue value={row.gross.value} unit={row.gross.unit} />
            ) : (
              '-'
            )}
          </div>
        ),
        footer: () => (
          <Col className="flex flex-col items-end gap-1 font-medium text-neutral">
            {!!totals.gross && (
              <div>
                <MeasuredValue
                  value={totals.gross.value}
                  unit={totals.gross.unit}
                />
              </div>
            )}
            {!!totals.eachCount && (
              <div>
                <MeasuredValue value={totals.eachCount} unit={enumUnit.Each} />
              </div>
            )}
          </Col>
        ),
      },
      {
        key: 'tare',
        label: t.transactionsView.actions.tareWeight,
        align: 'right',
        width: 'max-content',
        cell: (row) => (
          <div className="text-right">
            {row.tare ? (
              <MeasuredValue value={row.tare.value} unit={row.tare.unit} />
            ) : (
              '-'
            )}
          </div>
        ),
        footer: () => (
          <div className="text-right font-medium text-neutral">
            {totals.tare ? (
              <MeasuredValue
                value={totals.tare.value}
                unit={totals.tare.unit}
              />
            ) : (
              '-'
            )}
          </div>
        ),
      },
      {
        key: 'net',
        label: t.transactionsView.actions.netWeight,
        align: 'right',
        width: 'max-content',
        cell: (row) => (
          <div className="text-right">
            {row.net ? (
              <MeasuredValue value={row.net.value} unit={row.net.unit} />
            ) : (
              '-'
            )}
          </div>
        ),
        footer: () => (
          <Col className="flex flex-col items-end gap-1 font-medium text-neutral">
            {!!totals.net && (
              <div>
                <MeasuredValue
                  value={totals.net.value}
                  unit={totals.net.unit}
                />
              </div>
            )}
            {!!totals.eachCount && (
              <div>
                <MeasuredValue value={totals.eachCount} unit={enumUnit.Each} />
              </div>
            )}
          </Col>
        ),
      },
      {
        key: 'unitPrice',
        label: t.transactionsView.actions.unitPrice,
        align: 'right',
        width: 'max-content',
        cell: (row) =>
          row.unitPrice
            ? formatMoney(row.unitPrice.amount, row.unitPrice.currencyCode)
            : '-',
      },
      {
        key: 'total',
        label: t.transactionsView.actions.total,
        align: 'right',
        width: 'max-content',
        cell: (row) =>
          row.lineTotal
            ? formatMoney(row.lineTotal.amount, row.lineTotal.currencyCode)
            : '-',
        footer: () => (
          <div className="text-right font-medium text-neutral">
            {totals.lineTotal
              ? formatMoney(
                  totals.lineTotal.amount,
                  totals.lineTotal.currencyCode,
                )
              : '-'}
          </div>
        ),
      },
      {
        key: 'notes',
        label: t.transactionsView.actions.notes,
        width: 150,
        cell: (row) => (
          <span className="min-w-0 truncate text-neutral/70">
            {row.notes || '-'}
          </span>
        ),
      },
    ]}
  />
);
